import { ArrowUpRight } from "lucide-react";
import { BOOKS, AMAZON_STORE } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const ShelfBook = ({ book, testid }) => (
  <a
    href={book.href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group flex h-full flex-col"
  >
    <div className="relative flex h-64 items-end justify-center overflow-hidden rounded-t-sm border border-b-0 border-[var(--line)] bg-[#faf8f5] px-6 pt-8">
      <img
        src={book.cover}
        alt={book.title}
        loading="lazy"
        className="h-52 w-auto object-contain shadow-[0_18px_30px_-12px_rgba(15,23,42,0.45)] transition-transform duration-500 group-hover:-translate-y-1.5"
      />
    </div>
    {/* Shelf rail */}
    <div className="h-[3px] w-full bg-gradient-to-r from-[#9c7a33]/70 via-[#9c7a33]/30 to-transparent" />
    <div className="flex flex-1 flex-col border border-t-0 border-[var(--line)] bg-white px-5 pb-5 pt-4 transition-colors duration-500 group-hover:border-[#9c7a33]/50">
      <h4 className="font-display text-lg leading-tight text-[#1c2b4a]">
        {book.title}
      </h4>
      <span className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[#5c6474] transition-colors group-hover:text-[#9c7a33]">
        View on Amazon <ArrowUpRight className="h-3.5 w-3.5" />
      </span>
    </div>
  </a>
);

export default function Books() {
  return (
    <section
      id="books"
      data-testid="books-section"
      className="border-y border-[var(--line)] bg-white"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow testid="books-eyebrow">{BOOKS.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-7 font-display text-4xl font-light tracking-tight text-[#1c2b4a] md:text-5xl lg:text-[3.4rem]">
                {BOOKS.heading}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <a
              href={AMAZON_STORE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="books-store-link"
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[var(--line)] px-6 py-3 text-sm text-[#1c2b4a] transition-colors hover:border-[#9c7a33] hover:text-[#9c7a33]"
            >
              All 19 titles <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-14 lg:grid-cols-12 lg:gap-12">
          {/* Left — pinned author plate + featured title */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Reveal delay={0.05}>
                <figure data-testid="books-author-photo" className="relative">
                  <div className="absolute -left-3 -top-3 hidden h-full w-full rounded-sm border border-[#9c7a33]/45 lg:block" />
                  <div className="relative overflow-hidden rounded-sm border border-[var(--line)] bg-white p-2 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)]">
                    <img
                      src="/books-portrait.webp"
                      alt="Adonica Shaw holding her book, What No One Tells You About Motherhood"
                      className="h-[360px] w-full rounded-sm object-cover object-[center_28%]"
                    />
                  </div>
                  <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9c7a33]">
                    From the author
                  </figcaption>
                  <p className="mt-2 font-display text-xl italic leading-snug text-[#1c2b4a]">
                    “Every title begins with a truth I lived, then wrote down.”
                  </p>
                </figure>
              </Reveal>

              <Reveal delay={0.12}>
                <a
                  href={BOOKS.featured.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-featured"
                  className="group mt-10 flex gap-5 rounded-sm border border-[var(--line)] bg-[#faf8f5] p-5 transition-colors duration-500 hover:border-[#9c7a33]/50"
                >
                  <img
                    src={BOOKS.featured.cover}
                    alt={BOOKS.featured.title}
                    className="h-32 w-auto shrink-0 object-contain shadow-[0_14px_24px_-10px_rgba(15,23,42,0.45)] transition-transform duration-500 group-hover:-translate-y-1"
                  />
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9c7a33]">
                      Featured title
                    </span>
                    <h3 className="mt-2 font-display text-xl leading-tight text-[#1c2b4a]">
                      {BOOKS.featured.title}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-[#5c6474]">
                      {BOOKS.featured.subtitle}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-[#5c6474]">
                      {BOOKS.featured.blurb}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-[#1c2b4a] transition-colors group-hover:text-[#9c7a33]">
                      Read on Amazon <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </a>
              </Reveal>
            </div>
          </div>

          {/* Right — the shelf */}
          <div className="lg:col-span-8">
            <Reveal delay={0.08}>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#5c6474]">
                The Shelf — Selected Titles
              </p>
            </Reveal>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {BOOKS.grid.map((b, i) => (
                <Reveal key={b.title} delay={0.05 + i * 0.05}>
                  <ShelfBook book={b} testid={`book-${i}`} />
                </Reveal>
              ))}
            </div>

            {/* Forthcoming ledger */}
            <Reveal delay={0.1}>
              <p className="mt-14 font-mono text-[11px] uppercase tracking-[0.24em] text-[#5c6474]">
                Forthcoming
              </p>
            </Reveal>
            <div className="mt-4 border-t border-[var(--line)]">
              {BOOKS.upcoming.map((b, i) => (
                <Reveal key={b.title} delay={0.05 + i * 0.06}>
                  <a
                    href={b.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`book-upcoming-${i}`}
                    className="group flex items-center justify-between gap-6 border-b border-[var(--line)] py-5 transition-colors duration-300 hover:bg-[#faf8f5]"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9c7a33]">
                        Pre-order · {b.date}
                      </span>
                      <h4 className="font-display text-2xl text-[#1c2b4a]">
                        {b.title}
                      </h4>
                    </div>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-[#5c6474] transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#9c7a33]" />
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
